<template>
  <ion-page id="lichessfriend-page">
    <ion-content>

      <div v-if="showState == SHOW_EXPLAINTOUR">
        <ExplainTour :screen="explainTourScreen"
                      :wordsSpoken="explainTourWordsSpoken"
                      :wordsSpokenTrigger="explainTourWordsSpokenTrigger"
                    @toolbar-click="explainTourToolbarClick" />
      </div>

      <div v-show="showState == SHOW_FRIENDS">
        <TopLine :style="styleTopline" :title="friendTitleString" :microphoneIsMuted="microphoneIsMuted" @topline-click="toplineClick"/>
        <ion-list :style="styleMiddle" id="lichess-friends">
          <ion-item v-if="isLoading">
            <ion-label>
              <h1>{{ loadingFriendsString }}</h1>
            </ion-label>
          </ion-item>
          <ion-item>
            <ion-label color="danger" >
              {{ friendInfoString }}
            </ion-label>
          </ion-item>
          <ion-item v-for="item in onlineFriends" v-bind:key="item.index" @click="onClick(item.index)">
            <ion-label>
                "{{ item.index+1 }}" : {{ item.name }}
            </ion-label>
          </ion-item>
          <ion-item v-for="item in offlineFriends" v-bind:key="item.index">
            <ion-label>
              {{ offString }} : {{ item.name }}
            </ion-label>
          </ion-item>
        </ion-list>
        <ToolBar :style="styleToolbar" :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
      </div>
    </ion-content>
  </ion-page>
  
</template>

<script setup>

  import { IonPage, IonContent,IonList,IonItem,IonLabel  } from '@ionic/vue';
  import { buildStyleToolbar, buildStyleTopline,buildStyleMiddle,availableWidthHeight, scrollElement,
    calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'

  import { computed } from "@vue/reactivity"

  import { ref, watch } from 'vue'
  import ExplainTour from '@/components/ExplainTour.vue'
  import ToolBar from '@/components/ToolBar.vue'
  import { useConnectionStore } from '@/stores/connectionStore';
  import { useWindowSizeStore } from '@/stores/windowSizeStore';
  import { doStartupCheck } from '@/composables/startupCheck.js'
  import TopLine from '@/components/TopLine.vue'
  import { useGetData , getLichessData, generateErrorAlertText } from '../../composables/getData.js'
  import { parseWordsSaid , buildWordListFromChoiceList, parseNumberWords } from '@/composables/wordlistSupport.js'
  import { translateEnglishWords, translatePhrase } from '@/composables/translation.js';

  const emit = defineEmits(['where-next','word-list','mic-is-muted'])

  const props = defineProps({
      wordsSaid : {
        type : String,
        default : ""
      },
      wordsSaidTrigger : {
        type : Number,
        default : 0
      }
    })

  const wordsSaid = computed({get() {return props.wordsSaid}})
  const wordsSaidTrigger = computed({get() {return props.wordsSaidTrigger}})

  const connectionStore = useConnectionStore()
  const windowSizeStore = useWindowSizeStore()

  const microphoneIsMuted = ref(connectionStore.getMicrophoneIsMuted)
  emit("mic-is-muted",microphoneIsMuted.value)

  const isLoading = ref(true)

  const explainTourWordsSpoken = ref("")
  const explainTourScreen = ref("")
  const explainTourWordsSpokenTrigger = ref(0)
  
  const lichessAccessToken = connectionStore.getLichessAccessToken
  const isDark = ref(connectionStore.getDarkmode != "light")

  const offString = ref(translateEnglishWords("Off"))
  const friendTitleString = ref(translateEnglishWords("Lichess Friends"))

  const wordlistTrigger = ref(0)

  const wordlistFriends = "command scroll up down challenge explain done ignore mute listen one two three four five six seven eight nine ten eleven twelve thirteen fourteen fifteen sixteen seventeen eighteen nineteen twenty thirty forty fifty sixty seventy eighty ninety";
  const wordlist = ref(wordlistFriends)
  emit("word-list",wordlistFriends)

  const friendInfoString = ref(translateEnglishWords("To challenge friend : Say ' command challenge # ' as in ' command challenge 2 '"))
  const loadingFriendsString = ref(translatePhrase("Loading Lichess Friends List"))
  const SHOW_FRIENDS = 0
  const SHOW_EXPLAINTOUR = 1
  const showState = ref(SHOW_FRIENDS)

  const TOOLBAR_EXPLAIN = 0

  const toolbarElements = ref([
    { index : TOOLBAR_EXPLAIN, label : translateEnglishWords("Explain") ,disabled : false},
  ])
  const toolbarTrigger = ref(0)

  const styleToolbar = ref(buildStyleToolbar(availableWidthHeight()))
  const styleTopline = ref(buildStyleTopline(availableWidthHeight()))
  const styleMiddle = ref(buildStyleMiddle(availableWidthHeight()))

  var movelistHeight = 0
  var scrollTop = 0

  const onlineFriends = ref([])
  const offlineFriends = ref([])

   watch(wordsSaidTrigger, (newVal,oldVal) => {
     //console.log('*********** words said ',props.wordsSaid)
     processWords(props.wordsSaid)
   })

  watch(() => windowSizeStore.getWindowSize, () => {
    styleToolbar.value = buildStyleToolbar(availableWidthHeight())
    styleTopline.value = buildStyleTopline(availableWidthHeight())
    styleMiddle.value = buildStyleMiddle(availableWidthHeight())
  })

  doStartupCheck(false,afterStartupCallbackRoutine)

  function afterStartupCallbackRoutine(stuffChanged) {
    getFriends()
  }

 function toplineClick(whatClicked) {
    //console.log("topline",whatClicked)
    switch (whatClicked) {
      case "done" :
        processWords("command done")
        break;
      case "microphone" :
        microphoneIsMuted.value = !microphoneIsMuted.value 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(microphoneIsMuted.value)
        break

    }
  }

  // START Explain Tour Routines

  function explainTourInitialize(errorText) {
    showState.value = SHOW_EXPLAINTOUR
    explainTourScreen.value = (errorText == null) ? "lichess_friend" : ("ERROR:" + errorText);
    wordlist.value = connectionStore.getExplainTourWords
    emit("word-list",wordlist.value)
    wordlistTrigger.value++
  }

  function explainTourToolbarClick(whatClicked) {
    switch (whatClicked) {

      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "done" :
        showState.value = SHOW_FRIENDS
        wordlist.value = wordlistFriends
        emit("word-list",wordlist.value)
        wordlistTrigger.value++
        break
    }
  }

  // END Explain Tour Routines

  function toolbarClick(arrayIndex) {
    switch (arrayIndex) {
      case TOOLBAR_EXPLAIN :
        processWords("command explain")
        break
    }
  }

  function onClick(index) {
    const id = onlineFriends.value[index].id
    const name = onlineFriends.value[index].name
    connectionStore.setLichessFriend(id,name)
    connectionStore.setPlayType("friend")
    emit("where-next","/lichesschallenge")
    //router.replace("/lichesschallenge")
  }

  function parseCommands(words,currentWordIndex) {
    
    //console.log("in parse commands",words,currentWordIndex)
    var currentWord = (words.length > currentWordIndex) ? words[currentWordIndex] : "xyz"
    var nextWord = (words.length > currentWordIndex+1) ? words[currentWordIndex+1] : "xyz"
    var nextNextWord = (words.length > currentWordIndex+2) ? words[currentWordIndex+2] : "xyz"

    for (var i = 0; i < words.length; i++) {
        if (currentWord == "ignore") {
            return ["ignore",""];
        }
        if (currentWord == "explain") {
            return ["explain",""]
        }
        if (currentWord == "listen") {
            return ["listen",""]
        }
        if (currentWord == "mute") {
            return ["mute",""]
        }
        if (currentWord == "done") {
            return ["done",""]
        }
        if (currentWord == "scroll") {
            if (nextWord == "up") {
                return ["scroll", "up"]
            }
            if (nextWord == "down") {
                return ["scroll", "down"]
            }
        }
        
        // add choices here
        if (currentWord == "challenge") {
          const gameIndex = parseNumberWords(nextWord,nextNextWord)
          return ["challenge", gameIndex]
        }
       
        currentWord = nextWord;
        nextWord = nextNextWord;
        nextNextWord = (i < words.length - 3) ? words[i + 3] : "xyz";
    }
    return (["ignore",""]);
  }

  function processWords(text) {
    //console.log("text in engine Menu ",text)

    if (showState.value == SHOW_EXPLAINTOUR) {
      explainTourWordsSpoken.value = text
      explainTourWordsSpokenTrigger.value++
      return;
    }

    var returnArray = parseWordsSaid(text,parseCommands)
    //console.log("command found",returnArray)

    switch (returnArray[0]) {
      case "scroll" :
        scrollElement("lichess-friends",returnArray[1])
        break
      case "challenge" :
        const friendIndex = returnArray[1] - 1
        if (friendIndex < 0)
          break
        if (friendIndex > onlineFriends.value.length -1)
          break
        onClick(friendIndex)
        break
      case "explain" :
        explainTourInitialize(null)
        break
      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "done" :
        emit("where-next","/lichessmenu")
        //router.replace({ path: "/lichessmenu"})
        break
    }
  }

  function processJsonResponse (json) {
    //console.log(" in lichess friends callback",json)
    if (json.hasOwnProperty('error')) {
      showState.value = SHOW_EXPLAINTOUR
      explainTourInitialize(generateErrorAlertText(json))
      return
    }

    var ids = []
    const friends = json.split("\n");
    //console.log("***** Friends",friends)
    if (friends.length == 0)
            return;
    for (var i=0; i < friends.length; i++) {
      if (friends[i].length == 0)
        continue
      const oneFriend = JSON.parse(friends[i])
      if (oneFriend.hasOwnProperty('id'))
        ids.push(oneFriend['id'])
      //console.log("**** Friend ",i,oneFriend)
    }

    //console.log("IDS",ids)
    const idString = ids.join(",")
    //console.log("IDString",idString)
    
    getOnlineFriends(idString)

  }
  
  function getFriends() {
    const urlStr = "https://lichess.org/api/rel/following"
    getLichessData(urlStr, lichessAccessToken,null, processJsonResponse) 
  
    showState.value = SHOW_FRIENDS

  }

  function processOnlineFriendsJsonResponse(json) {
    //console.log(" online friends callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(generateErrorAlertText(json))
      return;
    }

    for (var loop = 0; loop < 2; loop++) {
      var onlineIndex = 0
      for (var i=0; i < json.length; i++) {
        var isOnline = false
        const name = json[i]['name']
        const id = json[i]['id']
        if (json[i].hasOwnProperty('online'))
          isOnline = (json[i]['online'] == true)
        
        if (loop == 0) {
          if (isOnline == false)
            continue
          //console.log("ONLINE ",id,name)
          const oneFriend = {
            index : onlineIndex,
            id : id,
            name : name
          }
          onlineFriends.value.push(oneFriend)
          onlineIndex++
        }
        else {
          if (isOnline == true)
            continue
          //console.log("OFFLINE ",id,name)
          const oneFriend = {
            index : i,
            name : name
          }
          offlineFriends.value.push(oneFriend)
        }
      }
    }
    isLoading.value = false
  }

  function getOnlineFriends(idString) {
    const urlStr = "https://lichess.org/api/users/status?ids=" + idString
    getLichessData(urlStr, lichessAccessToken,null, processOnlineFriendsJsonResponse) 
  
  }
</script>

<style scoped>

#gamehistory-selector-container {
  position : relative;
  border-style : solid;
  border-width: 2px;
  margin : 30px auto;
  padding : 15px 15px;
  line-height: 1.4em;
  width : 600px;
  height: calc(100vh - 125px);
  overflow-y: auto;
  font-family: "Nunito", sans-serif;   /* rounded */
  text-align: left;

}

</style>